import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import Dropdown from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons/DotsIcon'
import IconButton from 'components/v2/IconButton'
import List from 'components/v2/List'
import { ApolloCache, useMutation } from '@apollo/client'
import { SwitchIcon } from 'components/Icons/SwitchIcon'
import { ACTIVATE_BUSINESS_CLIENT, DEACTIVATE_BUSINESS_CLIENT } from 'features/BusinessClients/mutations'
import styled from 'styled-components'
import { WarningIcon } from 'components/Icons/WarningIcon'
import { LocationIcon2 } from 'components/Icons/LocationIcon2'
import { GTBusinessClient } from 'gql/types.generated'
import { PencilIcon } from 'components/Icons/PencilIcon'
import { PROCESS_BUSINESS_CLIENT_KREDITS_DISPERSION } from 'features/BusinessClients/mutations-legacy'
import SpreadKreditsModal from 'features/BusinessClients/components/SpreadKreditsModal'
import { isEmpty } from 'lodash'
import { CardMembershipIcon } from 'components/Icons/CardMembership'
import { canWrite } from 'features/Users/utils'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useLogin } from 'features/Login/hooks'
import { ActivateAction } from 'components/ActivateAction'

interface IBusinessClientActionsProps {
  businessClient: GTBusinessClient
}
const DeactivateWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledWarningIcon = styled(WarningIcon)`
  margin-left: 10px;
  fill: rgb(245, 46, 46);
`

export const toggleActivationLocalBusinessClient = (
  cache: ApolloCache<unknown>,
  clientId: string,
  value: boolean
): void => {
  cache.modify({
    id: cache.identify({
      __typename: 'BusinessClient',
      id: clientId
    }),
    fields: {
      isActive: () => value
    }
  })
}

export const BusinessClientListActions = ({ businessClient }: IBusinessClientActionsProps): ReactElement => {
  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const login = useLogin()
  const [isOpenSpreadKreditsModal, setIsOpenSpreadKreditsModal] = useState(false)
  const [activateBusinessClient, { loading: activateLoading, error: activateError }] = useMutation(
    ACTIVATE_BUSINESS_CLIENT,
    {
      update: (cache) => {
        toggleActivationLocalBusinessClient(cache, businessClient.id, true)
      }
    }
  )
  const [deactivateBusinessClient, { loading: deactivateLoading, error: deactivateError }] = useMutation(
    DEACTIVATE_BUSINESS_CLIENT,
    {
      update: (cache) => {
        toggleActivationLocalBusinessClient(cache, businessClient.id, false)
      }
    }
  )

  const [
    processBusinessClientKreditsDispersion,
    { loading: processBusinessClientKreditsDispersionLoading, error: processBusinessClientKreditsDispersionError }
  ] = useMutation(PROCESS_BUSINESS_CLIENT_KREDITS_DISPERSION, {
    onCompleted: () => {
      setIsOpenSpreadKreditsModal(false)
    },
    onError: () => {
      // do nothing
    }
  })

  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  const toggleClientStatus = (): void => {
    if (businessClient.isActive) {
      deactivateBusinessClient({
        variables: {
          id: businessClient.id
        }
      })
    } else {
      activateBusinessClient({
        variables: {
          id: businessClient.id
        }
      })
    }
  }

  const spreadCredits = (): void => {
    processBusinessClientKreditsDispersion({
      variables: {
        id: businessClient.id
      }
    })
  }

  const openSpreadCreditsModal = (): void => {
    setIsOpenSpreadKreditsModal(true)
  }

  const isToggleClientStatusDisabled = businessClient.isActive && businessClient.numberOfCustomers > 0
  const canSpreadCredits =
    (businessClient.isActive || !isEmpty(businessClient.kreditsSettings)) &&
    canWrite(FEATURES.CORPORATE_KREDITS, login.accessToken.policies)
  const loading = activateLoading || deactivateLoading
  const error = activateError || deactivateError

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): TSFixMe => setOpenDropdown(false)}>
        <IconButton icon={<DotsIcon />} noBorder smallPadding onClick={toggleDropdown} />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.ItemLink href={`/business-sites/new?businessClientId=${businessClient.id}`}>
              <List.Icon>
                <LocationIcon2 />
              </List.Icon>
              <FormattedMessage id="businessClient.actions.add" defaultMessage="Add Client Site" />
            </List.ItemLink>
            <List.ItemLink href={`/business-clients/${businessClient.id}`}>
              <List.Icon>
                <PencilIcon />
              </List.Icon>
              <FormattedMessage id="businessClient.actions.edit" defaultMessage="Edit Client" />
            </List.ItemLink>
            <List.Item action onClick={toggleClientStatus} loading={loading} disabled={isToggleClientStatusDisabled}>
              <List.Icon>
                <SwitchIcon />
              </List.Icon>
              <ActivateAction isActive={businessClient.isActive} error={error} />
            </List.Item>
            <List.Item
              action
              onClick={(): void => {
                if (canSpreadCredits) {
                  openSpreadCreditsModal()
                }
              }}
              loading={processBusinessClientKreditsDispersionLoading}
              disabled={!canSpreadCredits}
            >
              <List.Icon>
                <CardMembershipIcon />
              </List.Icon>
              <DeactivateWrapper>
                <FormattedMessage id="businessClient.actions.spreadCredits" defaultMessage="Spread Credits" />
                {activateError && <StyledWarningIcon />}
              </DeactivateWrapper>
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isOpenSpreadKreditsModal ? (
        <SpreadKreditsModal
          loading={processBusinessClientKreditsDispersionLoading}
          error={processBusinessClientKreditsDispersionError}
          onClose={(): void => setIsOpenSpreadKreditsModal(false)}
          clientId={businessClient.id}
          onSubmit={spreadCredits}
        />
      ) : null}
    </>
  )
}
